import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Bookmark, Share2, Music2, Plus, Check, MoreHorizontal, Play, Volume2, VolumeX, Loader2, Tv } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { castVideo } from "@/lib/cast";
import { fmtCount } from "@/lib/vibe";
import { useVibe, VAvatar, useNeedLogin, vErr } from "./VibeLayout";
import { CommentsSheet, ShareSheet, MoreSheet } from "./VibeSheets";

let globalMuted = true;

const Rail = ({ icon, label, onClick, active, testId, color }) => (
  <button onClick={onClick} data-testid={testId} className="flex flex-col items-center gap-0.5 active:scale-90 transition-transform">
    <span className={`w-11 h-11 rounded-full flex items-center justify-center bg-black/25 backdrop-blur-sm ${active ? color : "text-white"}`}>{icon}</span>
    <span className="text-[11px] font-bold drop-shadow">{label}</span>
  </button>
);

export function VideoSlide({ v: initial, active, preload }) {
  const { me } = useVibe();
  const need = useNeedLogin();
  const nav = useNavigate();
  const [v, setV] = useState(initial);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(globalMuted);
  const [state, setState] = useState("loading");
  const [sheet, setSheet] = useState("");
  const [burst, setBurst] = useState(false);
  const ref = useRef(null);
  const watch = useRef({ t: 0, start: 0 });

  useEffect(() => {
    const el = ref.current; if (!el) return;
    if (active) {
      el.muted = globalMuted; setMuted(globalMuted);
      el.play().then(() => setPaused(false)).catch(() => { el.muted = true; setMuted(true); el.play().catch(() => setPaused(true)); });
      watch.current.start = Date.now();
    } else {
      el.pause();
      if (watch.current.start) {
        const ms = Date.now() - watch.current.start; watch.current.start = 0;
        api.post(`/vibe/videos/${v.id}/view`, { watch_ms: ms }).catch(() => {});
      }
    }
  }, [active, v.id]);

  const toggle = () => { const el = ref.current; if (el.paused) { el.play(); setPaused(false); } else { el.pause(); setPaused(true); } };
  const like = async () => {
    if (need()) return;
    setV((x) => ({ ...x, liked: !x.liked, likes: x.likes + (x.liked ? -1 : 1) }));
    try { await api.post(`/vibe/videos/${v.id}/like`); } catch (e) { vErr(e); }
  };
  const dbl = () => { if (!v.liked) like(); setBurst(true); setTimeout(() => setBurst(false), 700); };
  const save = async () => {
    if (need()) return;
    setV((x) => ({ ...x, saved: !x.saved, saves: x.saves + (x.saved ? -1 : 1) }));
    try { await api.post(`/vibe/videos/${v.id}/save`); } catch (e) { vErr(e); }
  };
  const follow = async () => {
    if (need()) return;
    try { const { data } = await api.post(`/vibe/u/${v.creator.user_id}/follow`); setV((x) => ({ ...x, following: data.following })); } catch (e) { vErr(e); }
  };
  const cast = async () => { if (!(await castVideo(ref.current))) { toast("Casting isn't supported in this browser — try Jixxeo Mirroring", { action: { label: "Open", onClick: () => nav("/mirror") } }); } };
  const mine = me?.profile?.user_id === v.creator?.user_id;
  const tap = useRef(0);
  const onTap = () => { const n = Date.now(); if (n - tap.current < 280) { dbl(); tap.current = 0; } else { tap.current = n; setTimeout(() => { if (tap.current === n) toggle(); }, 290); } };

  return (
    <div className="relative h-full w-full snap-start snap-always bg-black overflow-hidden" data-testid={`vibe-slide-${v.id}`} data-active={active ? "yes" : "no"}>
      <video ref={ref} src={v.video_url} poster={v.thumb_url} loop playsInline muted={muted} preload={active || preload ? "auto" : "none"}
        onWaiting={() => setState("loading")} onPlaying={() => setState("ok")} onCanPlay={() => setState((s) => (s === "loading" ? "ok" : s))} onError={() => setState("error")}
        className="absolute inset-0 w-full h-full object-contain" onClick={onTap} data-testid="vibe-video" x-webkit-airplay="allow" />
      {state === "loading" && active && <Loader2 className="absolute top-1/2 left-1/2 -ml-5 -mt-5 w-10 h-10 animate-spin text-white/70 pointer-events-none" />}
      {state === "error" && <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70 pointer-events-none" data-testid="vibe-video-error">Couldn't load this video</div>}
      {paused && state !== "error" && <Play className="absolute top-1/2 left-1/2 -ml-8 -mt-8 w-16 h-16 text-white/80 pointer-events-none" fill="currentColor" />}
      {burst && <Heart className="absolute top-1/2 left-1/2 -ml-14 -mt-14 w-28 h-28 text-[#FE2C55] animate-ping pointer-events-none" fill="currentColor" />}
      <button onClick={() => { globalMuted = !muted; setMuted(!muted); ref.current.muted = !muted; }} data-testid="vibe-mute" aria-label="Sound" className="absolute top-14 right-3 w-9 h-9 rounded-full bg-black/30 flex items-center justify-center">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
      <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4 z-10">
        <div className="relative mb-2">
          <Link to={`/vibe/u/${v.creator?.username}`} data-testid="vibe-creator-avatar"><VAvatar p={v.creator} size={46} ring /></Link>
          {!mine && !v.following && <button onClick={follow} data-testid="vibe-follow-btn" aria-label="Follow" className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#FE2C55] flex items-center justify-center"><Plus className="w-3.5 h-3.5" strokeWidth={3} /></button>}
          {!mine && v.following && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white text-[#FE2C55] flex items-center justify-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>}
        </div>
        <Rail icon={<Heart className="w-7 h-7" fill={v.liked ? "currentColor" : "none"} />} label={fmtCount(v.likes)} onClick={like} active={v.liked} color="text-[#FE2C55]" testId="vibe-like-btn" />
        <Rail icon={<MessageCircle className="w-7 h-7" />} label={fmtCount(v.comments)} onClick={() => setSheet("comments")} testId="vibe-comments-btn" />
        <Rail icon={<Bookmark className="w-7 h-7" fill={v.saved ? "currentColor" : "none"} />} label={fmtCount(v.saves)} onClick={save} active={v.saved} color="text-[#FFC700]" testId="vibe-save-btn" />
        <Rail icon={<Share2 className="w-7 h-7" />} label={fmtCount(v.shares)} onClick={() => setSheet("share")} testId="vibe-share-btn" />
        <Rail icon={<Tv className="w-6 h-6" />} label="Cast" onClick={cast} testId="vibe-cast-btn" />
        <Rail icon={<MoreHorizontal className="w-6 h-6" />} label="" onClick={() => setSheet("more")} testId="vibe-more-btn" />
        {v.sound && <Link to={`/vibe/sound/${v.sound.id}`} data-testid="vibe-sound-disc" className="w-11 h-11 rounded-full bg-gradient-to-br from-[#333] to-black border-[6px] border-[#222] flex items-center justify-center animate-[spin_5s_linear_infinite]"><Music2 className="w-4 h-4" /></Link>}
      </div>
      <div className="absolute left-0 right-16 bottom-0 p-4 pb-5 bg-gradient-to-t from-black/80 via-black/30 to-transparent z-10">
        <Link to={`/vibe/u/${v.creator?.username}`} className="font-black text-base drop-shadow" data-testid="vibe-creator-name">@{v.creator?.username}</Link>
        {v.caption && <p className="text-sm mt-1 leading-snug drop-shadow line-clamp-3" data-testid="vibe-caption">
          {v.caption.split(/(#\w+)/g).map((t, i) => t.startsWith("#") ? <Link key={i} to={`/vibe/tag/${t.slice(1).toLowerCase()}`} className="font-bold">{t}</Link> : t)}
        </p>}
        {v.sound && (
          <div className="flex items-center gap-2 mt-2">
            <Link to={`/vibe/sound/${v.sound.id}`} className="flex items-center gap-1.5 text-xs min-w-0 flex-1" data-testid="vibe-sound-title"><Music2 className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{v.sound.title}</span></Link>
            <button onClick={() => nav(`/vibe/create?sound=${v.sound.id}`)} data-testid="vibe-use-sound-btn" className="shrink-0 bg-white/20 backdrop-blur rounded-full px-3 py-1.5 text-xs font-black hover:bg-white/30">Use this sound</button>
          </div>
        )}
        <div className="text-[10px] text-white/60 mt-1.5 mono">{fmtCount(v.views)} views</div>
      </div>
      {sheet === "comments" && <CommentsSheet v={v} onClose={() => setSheet("")} onCount={(d) => setV((x) => ({ ...x, comments: x.comments + d }))} />}
      {sheet === "share" && <ShareSheet v={v} onClose={() => setSheet("")} onShared={() => setV((x) => ({ ...x, shares: x.shares + 1 }))} />}
      {sheet === "more" && <MoreSheet v={v} mine={mine} onClose={() => setSheet("")} onCast={cast} />}
    </div>
  );
}

export function FeedList({ load, deps = [], empty }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(true);
  const box = useRef(null);
  const more = async (reset) => {
    setBusy(true);
    try {
      const d = await load(reset ? 0 : items.length);
      setItems((xs) => { const base = reset ? [] : xs; const seen = new Set(base.map((x) => x.id)); return [...base, ...d.videos.filter((x) => !seen.has(x.id))]; });
      if (!d.has_more || !d.videos.length) setDone(true);
    } catch (e) { vErr(e); }
    setBusy(false);
  };
  useEffect(() => { setItems([]); setDone(false); setActive(0); box.current?.scrollTo(0, 0); more(true); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = box.current; if (!el) return;
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.idx)); }), { root: el, threshold: 0.6 });
    el.querySelectorAll("[data-idx]").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [items]);
  useEffect(() => { if (!done && !busy && items.length && active >= items.length - 3) more(false); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e) => { if (!box.current || !["ArrowDown", "ArrowUp"].includes(e.key) || e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return; e.preventDefault(); box.current.scrollBy({ top: (e.key === "ArrowDown" ? 1 : -1) * box.current.clientHeight, behavior: "smooth" }); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, []);
  if (!busy && !items.length) return <div className="h-full flex items-center justify-center text-center px-8 text-white/60 text-sm" data-testid="vibe-feed-empty">{empty}</div>;
  return (
    <div ref={box} className="h-full overflow-y-scroll snap-y snap-mandatory overscroll-contain [scrollbar-width:none]" data-testid="vibe-feed">
      {items.map((v, i) => <div key={v.id} data-idx={i} className="h-full"><VideoSlide v={v} active={i === active} preload={i === active + 1} /></div>)}
      {busy && <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/60" /></div>}
    </div>
  );
}
